import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import PrintIcon from '@mui/icons-material/Print';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import InfoIcon from '@mui/icons-material/Info';
import { useJob } from '../context/JobContext';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Search, CalendarDays as ContactLogoIcon } from 'lucide-react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function Contatto() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const { addJob } = useJob();

  // Dossier Modal State
  const [dossierModalOpen, setDossierModalOpen] = useState(false);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/contatto');
      setList(res.data);
    } catch (error) {
      console.error('Error fetching contatto list:', error);
      toast.error('Errore nel caricamento dei dati Contatto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredList = list
    .filter((item) => {
      if (!filterName) return true;
      const search = filterName.toLowerCase();
      return (
        item.nome.toLowerCase().includes(search) ||
        item.cognome.toLowerCase().includes(search) ||
        item.email.toLowerCase().includes(search) ||
        item.telefono.toLowerCase().includes(search) ||
        item.pec.toLowerCase().includes(search) ||
        item.rif_dossier.toString().includes(search)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'id' || orderBy === 'rif_dossier') {
        return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
      }
      const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
      const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });

  const handleSelectAll = () => {
    const newSelected = filteredList.map((n) => n.id);
    setSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredList.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePrintPdf = async () => {
    try {
      const startRes = await api.post('/contatto/report', { ids: selected });
      const jobId = startRes.data.jobId;
      addJob(jobId, 'contatto');
      setSelected([]);
      toast.success('Generazione PDF avviata');
    } catch (error) {
      console.error('Error starting report job:', error);
      toast.error('Errore nell\'avvio della generazione del PDF');
    }
  };

  const handleDossierClick = async (event, dossierId) => {
    event.stopPropagation(); // Prevent row click
    if (!dossierId) return;

    setLoadingDossier(true);
    setDossierModalOpen(true);
    try {
      const res = await api.get(`/dossier/${dossierId}`);
      setSelectedDossier(res.data);
    } catch (error) {
      console.error('Error fetching dossier:', error);
      toast.error('Errore nel caricamento del dossier');
      setDossierModalOpen(false);
    } finally {
      setLoadingDossier(false);
    }
  };

  const handleCloseDossierModal = () => {
    setDossierModalOpen(false);
    setSelectedDossier(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ContactLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Contatto (Remoto)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Elenco contatti remoti con selezione massiva e dossier collegati
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
            disabled={filteredList.length === 0 || (selected.length === filteredList.length)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Seleziona tutti ({filteredList.length})
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={handlePrintPdf}
            disabled={selected.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            Stampa PDF
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca per nome, email, telefono..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < filteredList.length}
                    checked={filteredList.length > 0 && selected.length === filteredList.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'nome'}
                    direction={orderBy === 'nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('nome')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cognome'}
                    direction={orderBy === 'cognome' ? order : 'asc'}
                    onClick={() => handleRequestSort('cognome')}
                  >
                    Cognome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'telefono'}
                    direction={orderBy === 'telefono' ? order : 'asc'}
                    onClick={() => handleRequestSort('telefono')}
                  >
                    Telefono
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'pec'}
                    direction={orderBy === 'pec' ? order : 'asc'}
                    onClick={() => handleRequestSort('pec')}
                  >
                    PEC
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'rif_dossier'}
                    direction={orderBy === 'rif_dossier' ? order : 'asc'}
                    onClick={() => handleRequestSort('rif_dossier')}
                  >
                    Rif. Dossier
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => {
                    const isSelected = selected.indexOf(item.id) !== -1;
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        role="checkbox"
                        aria-checked={isSelected}
                        selected={isSelected}
                        onClick={(event) => handleClick(event, item.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isSelected}
                          />
                        </TableCell>
                        <TableCell>{item.id}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{item.nome}</TableCell>
                        <TableCell>{item.cognome}</TableCell>
                        <TableCell>{item.email}</TableCell>
                        <TableCell>{item.telefono}</TableCell>
                        <TableCell>{item.pec}</TableCell>
                        <TableCell>
                          {item.rif_dossier ? (
                            <Button
                              variant="contained"
                              color="info"
                              size="small"
                              startIcon={<InfoIcon />}
                              onClick={(e) => handleDossierClick(e, item.rif_dossier)}
                              disableElevation
                              sx={{ 
                                textTransform: 'none', 
                                px: 2
                              }}
                            >
                              Info
                            </Button>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      {/* Dossier Detail Modal */}
      <Dialog
        open={dossierModalOpen}
        onClose={handleCloseDossierModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Dettagli Dossier #{selectedDossier?.id}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDossier ? (
            <Typography sx={{ p: 3, textAlign: 'center' }}>Caricamento...</Typography>
          ) : selectedDossier ? (
            <Grid container spacing={2}>
              {/* Customer Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                  Cliente
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Nome/Ragione Sociale</Typography>
                    <Typography variant="body1">
                      {selectedDossier.customer_ragione_sociale || 
                       `${selectedDossier.customer_name || ''} ${selectedDossier.customer_surname || ''}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedDossier.customer_email || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Telefono</Typography>
                    <Typography variant="body1">{selectedDossier.customer_telephone || '-'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Responsabili */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                  Responsabili
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Commerciale</Typography>
                    <Typography variant="body1">{selectedDossier.responsabile_commerciale || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Contabile</Typography>
                    <Typography variant="body1">{selectedDossier.responsabile_contabile || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Upsell</Typography>
                    <Typography variant="body1">{selectedDossier.responsabile_upsell || '-'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Dossier Status/Dates */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                  Stato e Date
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Data Creazione</Typography>
                    <Typography variant="body1">
                      {selectedDossier.created_at ? new Date(selectedDossier.created_at).toLocaleDateString() : '-'}
                    </Typography>
                  </Grid>
                  {/* Add more fields here if available in Dossier model */}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Typography color="error">Impossibile caricare i dettagli del dossier.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDossierModal} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
